import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Loader2, AlertTriangle } from "lucide-react"
import { parseQuestions, addQuestions } from "@/lib/api"
import type { ParsedQuestion } from "@/types/questionnaire-types"
import { toast } from "sonner"
import { cn } from "@/lib/utils"

// ===================
// Types
// ===================

interface ParsedQuestionRow extends ParsedQuestion {
  id: number
  selected: boolean
}

interface PasteParseDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  artifactId: string
  onSuccess: () => void
}

type Step = 'paste' | 'review'

// ===================
// Component
// ===================

export function PasteParseDialog({
  open,
  onOpenChange,
  artifactId,
  onSuccess,
}: PasteParseDialogProps) {
  const [step, setStep] = useState<Step>('paste')
  const [rawText, setRawText] = useState("")
  const [isParsing, setIsParsing] = useState(false)
  const [isImporting, setIsImporting] = useState(false)
  const [parsedQuestions, setParsedQuestions] = useState<ParsedQuestionRow[]>([])
  const [warnings, setWarnings] = useState<string[]>([])

  const selectedCount = parsedQuestions.filter((q) => q.selected).length

  const resetState = () => {
    setStep('paste')
    setRawText("")
    setParsedQuestions([])
    setWarnings([])
  }

  const handleClose = (value: boolean) => {
    if (!value) resetState()
    onOpenChange(value)
  }

  const handleParse = async () => {
    try {
      setIsParsing(true)
      const result = await parseQuestions(artifactId, rawText.trim())
      setParsedQuestions(
        result.parsed.map((q, i) => ({ ...q, selected: true, id: i }))
      )
      setWarnings(result.warnings ?? [])
      setStep('review')
    } catch {
      toast.error("Failed to parse questions. Please try again.")
    } finally {
      setIsParsing(false)
    }
  }

  const toggleQuestion = (id: number) => {
    setParsedQuestions((prev) =>
      prev.map((q) => (q.id === id ? { ...q, selected: !q.selected } : q))
    )
  }

  const handleImport = async () => {
    const selected = parsedQuestions.filter((q) => q.selected)
    if (selected.length === 0) return

    try {
      setIsImporting(true)
      await addQuestions(
        artifactId,
        selected.map((q) => ({
          number: q.number,
          prompt: q.prompt,
          responseType: q.responseType,
          mandatory: true,
          wordLimit: q.wordLimit,
          groupTitle: q.groupTitle,
        }))
      )
      toast.success(`Imported ${selected.length} question${selected.length !== 1 ? "s" : ""}`)
      onSuccess()
      handleClose(false)
    } catch {
      toast.error("Failed to import questions")
    } finally {
      setIsImporting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {step === 'paste' ? "Paste Questions" : "Review Parsed Questions"}
          </DialogTitle>
          <DialogDescription>
            {step === 'paste'
              ? "Paste the numbered questions from the tender document. AI will extract the structure."
              : `${selectedCount} of ${parsedQuestions.length} questions selected for import.`}
          </DialogDescription>
        </DialogHeader>

        {step === 'paste' ? (
          <>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="paste-text">Tender questions</Label>
                <Textarea
                  id="paste-text"
                  placeholder={"1.1 Describe your experience delivering infrastructure projects of similar scale and complexity within the last 5 years. (500 words max)\n\n1.2 What is your approach to quality management? (750 words)\n\n1.3 Do you hold ISO 9001 certification?"}
                  value={rawText}
                  onChange={(e) => setRawText(e.target.value)}
                  rows={14}
                  className="font-mono text-xs"
                />
                <p className="text-meta">
                  Supports numbered lists with word limits. AI will extract question numbers, text, response types, and word limits.
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => handleClose(false)}>
                Cancel
              </Button>
              <Button onClick={() => void handleParse()} disabled={isParsing || !rawText.trim()}>
                {isParsing && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                Parse Questions
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <div className="space-y-3 py-4">
              {/* Warnings */}
              {warnings.length > 0 && (
                <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50/50 px-3 py-2">
                  <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
                  <div className="space-y-0.5">
                    {warnings.map((w, i) => (
                      <p key={i} className="text-xs text-amber-700">{w}</p>
                    ))}
                  </div>
                </div>
              )}

              {/* Parsed questions list */}
              <div className="max-h-[50vh] overflow-y-auto space-y-2 pr-1">
                {parsedQuestions.map((q) => (
                  <div
                    key={q.id}
                    className={cn(
                      "border rounded-lg p-3 transition-opacity",
                      !q.selected && "opacity-50"
                    )}
                  >
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        checked={q.selected}
                        onChange={() => toggleQuestion(q.id)}
                        className="mt-1 shrink-0"
                      />
                      <div className="flex-1 min-w-0 space-y-1.5">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-xs font-mono text-muted-foreground">
                            {q.number}
                          </span>
                          <Badge variant="default" className="text-[10px]">
                            Rich text
                          </Badge>
                          {q.wordLimit && (
                            <span className="text-[10px] text-muted-foreground">
                              {q.wordLimit} words
                            </span>
                          )}
                          {q.confidence < 0.7 && (
                            <Badge variant="warning" className="text-[10px]">
                              Low confidence
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm leading-snug">{q.prompt}</p>
                        {q.groupTitle && (
                          <span className="text-[10px] text-muted-foreground">
                            Group: {q.groupTitle}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setStep('paste')}>
                Back
              </Button>
              <Button
                onClick={() => void handleImport()}
                disabled={isImporting || selectedCount === 0}
              >
                {isImporting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                Import {selectedCount} Question{selectedCount !== 1 ? "s" : ""}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
